import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { BlockNoteView } from '@blocknote/mantine';
import { SuggestionMenuController, useCreateBlockNote } from '@blocknote/react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ChatPanel } from './AgentChat';
import { AGENTS } from './AgentChat/agents';
import type { Agent } from './AgentChat/agents';
import { getCustomSlashMenuItems } from '../slashMenu';
import type { OpenAgentDetail } from '../slashMenu/agentCommands';
import { getAuthHeaders } from '../auth';
import { useAiDisclosure } from './AiDisclosureModal';
import { uploadVideo } from '../slashMenu/videoUpload';

// API configuration
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8788';
const REALTIME_URL = import.meta.env.VITE_REALTIME_URL || 'ws://localhost:8789';

type ChatMode = 'sidebar' | 'modal';

export function Editor() {
  const { pageId } = useParams<{ pageId: string }>();
  const [title, setTitle] = useState('Untitled');
  const [ydoc] = useState(() => new Y.Doc());
  const [provider, setProvider] = useState<WebsocketProvider | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>('sidebar');
  const [pendingAgent, setPendingAgent] = useState<Agent | null>(null);
  const { requireDisclosure, AcceptanceModal } = useAiDisclosure();

  // Create BlockNote editor with Yjs collaboration
  const editor = useCreateBlockNote({
    collaboration: {
      provider: provider || undefined,
      fragment: ydoc.getXmlFragment('document'),
      user: {
        name: 'Anonymous',
        color: '#' + Math.floor(Math.random() * 16777215).toString(16),
      },
    },
  });

  // Memoize the slash menu getItems function
  const getSlashMenuItems = useMemo(() => getCustomSlashMenuItems(editor), [editor]);

  // Listen for agent open events dispatched by slash commands (e.g. /verhaal)
  const handleAgentEvent = useCallback((e: Event) => {
    const detail = (e as CustomEvent<OpenAgentDetail>).detail;
    if (!detail?.agentId) return;

    // Find the matching Agent from the AGENTS registry
    const matched = AGENTS.find(
      (a) => a.id.toLowerCase() === detail.agentId.toLowerCase()
    );
    if (matched) {
      setPendingAgent(matched);
      setChatOpen(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('bb-docs:open-agent', handleAgentEvent);
    const handleVideoEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ aiGenerated: boolean }>).detail;
      if (detail.aiGenerated) {
        requireDisclosure(() => uploadVideo(editor, { aiGenerated: true }));
      } else {
        uploadVideo(editor, { aiGenerated: false });
      }
    };
    window.addEventListener('bb-docs:insert-video', handleVideoEvent);
    return () => {
      window.removeEventListener('bb-docs:open-agent', handleAgentEvent);
      window.removeEventListener('bb-docs:insert-video', handleVideoEvent);
    };
  }, [handleAgentEvent, editor, requireDisclosure]);

  useEffect(() => {
    if (!pageId) return;

    // Connect to realtime sync
    const wsProvider = new WebsocketProvider(
      `${REALTIME_URL}/sync`,
      pageId,
      ydoc
    );

    setProvider(wsProvider);

    // Load page metadata
    fetch(`${API_URL}/v1/pages/${pageId}`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.page?.title) {
          setTitle(data.page.title);
        }
      })
      .catch(console.error);

    return () => {
      wsProvider.destroy();
    };
  }, [pageId, ydoc]);

  const handleTitleChange = async (newTitle: string) => {
    setTitle(newTitle);

    // Save title to API
    await fetch(`${API_URL}/v1/pages/${pageId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ title: newTitle }),
    });
  };

  const toggleChat = () => {
    setChatOpen((prev) => !prev);
  };

  const toggleChatMode = () => {
    setChatMode((prev) => (prev === 'sidebar' ? 'modal' : 'sidebar'));
  };

  return (
    <div className={`editor-outer ${chatOpen && chatMode === 'sidebar' ? 'with-chat-sidebar' : ''}`}>
      {AcceptanceModal}
      <div className="editor-container">
        <div className="editor-toolbar">
              <div className="editor-toolbar-left">
                <button
                  className="agent-chat-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('bb-docs:insert-video', { detail: { aiGenerated: false } }))}
                  title="Upload video"
                >
                  <span className="agent-chat-btn-icon">{'\u{25B6}'}</span>
                  <span className="agent-chat-btn-label">Video</span>
                </button>
                <button
                  className="agent-chat-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('bb-docs:insert-video', { detail: { aiGenerated: true } }))}
                  title="AI-generated video (EU AI Act labelled)"
                >
                  <span className="agent-chat-btn-icon">{'\u{1F3AC}'}</span>
                  <span className="agent-chat-btn-label">AI Video</span>
                </button>
              </div>
          <div className="editor-toolbar-right">
            <button
              className={`agent-chat-btn ${chatOpen ? 'active' : ''}`}
              onClick={toggleChat}
              title="Toggle Agent Chat"
            >
              <span className="agent-chat-btn-icon">{'\u2726'}</span>
              <span className="agent-chat-btn-label">Agent</span>
              <span className={`agent-status-dot ${chatOpen ? 'connected' : ''}`} />
            </button>
            {chatOpen && (
              <button
                className="chat-mode-btn"
                onClick={toggleChatMode}
                title={chatMode === 'sidebar' ? 'Switch to modal' : 'Switch to sidebar'}
              >
                {chatMode === 'sidebar' ? '\u{2B1C}' : '\u{25A8}'}
              </button>
            )}
          </div>
        </div>

        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="page-title-input"
          placeholder="Untitled"
        />
        <div className="blocknote-wrapper">
          <BlockNoteView editor={editor} theme="light" slashMenu={false}>
            <SuggestionMenuController
              triggerCharacter="/"
              getItems={getSlashMenuItems}
            />
          </BlockNoteView>
        </div>
      </div>

      {chatOpen && (
        <ChatPanel
          mode={chatMode}
          editor={editor}
          onClose={() => setChatOpen(false)}
          initialAgent={pendingAgent}
        />
      )}
    </div>
  );
}
